import { getToolName } from "ai"

import {
    ASSISTANT_TOOL_NAMES,
    ASSISTANT_TOOL_OUTPUT_STATUSES,
    ASSISTANT_TOOL_OUTPUT_TYPES,
} from "~components/outfitter-assistant/assistant-tool-constants"
import type { AssistantChartPresentation } from "~components/outfitter-assistant/assistant-chart-tool.helpers"
import type { AssistantPlanPresentation } from "~components/outfitter-assistant/assistant-plan-tool.helpers"
import {
    getInputRecord,
    getOutputRecord,
    type AnyToolUIPart,
} from "~components/shared/chat/tool-ui-part-helpers"
import COPY from "~config/copy-constants"

const TOOL_STATES = {
    INPUT_STREAMING: "input-streaming",
    INPUT_AVAILABLE: "input-available",
    APPROVAL_REQUESTED: "approval-requested",
    APPROVAL_RESPONDED: "approval-responded",
    OUTPUT_AVAILABLE: "output-available",
    OUTPUT_ERROR: "output-error",
    OUTPUT_DENIED: "output-denied",
} as const
export const TOOL_CALL_VARIANTS = {
    RUNNING: "running",
    DONE: "done",
    ERROR: "error",
    DENIED: "denied",
} as const

export type ToolCallVariant =
    (typeof TOOL_CALL_VARIANTS)[keyof typeof TOOL_CALL_VARIANTS]

const VERB_BY_VARIANT: Record<ToolCallVariant, string> = {
    [TOOL_CALL_VARIANTS.RUNNING]: COPY.AI_CHAT_TOOL_CALL_RUNNING,
    [TOOL_CALL_VARIANTS.DONE]: COPY.AI_CHAT_TOOL_CALL_DONE,
    [TOOL_CALL_VARIANTS.ERROR]: COPY.AI_CHAT_TOOL_CALL_FAILED,
    [TOOL_CALL_VARIANTS.DENIED]: COPY.AI_CHAT_TOOL_CALL_DENIED,
}

/** Per-tool running/done copy; falls back to the generic verbs when absent. */
const TOOL_SPECIFIC_VERBS: Readonly<
    Record<string, { running: string; done: string }>
> = {
    [ASSISTANT_TOOL_NAMES.NAVIGATE_TO_PAGE]: {
        running: COPY.AI_CHAT_TOOL_NAVIGATE_RUNNING,
        done: COPY.AI_CHAT_TOOL_NAVIGATE_DONE,
    },
    [ASSISTANT_TOOL_NAMES.CREATE_MESSAGE_TEMPLATE]: {
        running: COPY.AI_CHAT_TOOL_CREATE_MESSAGE_TEMPLATE_RUNNING,
        done: COPY.AI_CHAT_TOOL_CREATE_MESSAGE_TEMPLATE_DONE,
    },
    [ASSISTANT_TOOL_NAMES.EDIT_EMAIL_TEMPLATE]: {
        running: COPY.AI_CHAT_TOOL_EMAIL_TEMPLATE_RUNNING,
        done: COPY.AI_CHAT_TOOL_EMAIL_TEMPLATE_DONE,
    },
    [ASSISTANT_TOOL_NAMES.PREPARE_OUTFITTER_FORM]: {
        running: COPY.AI_CHAT_TOOL_OUTFITTER_FORM_PREPARE_RUNNING,
        done: COPY.AI_CHAT_TOOL_OUTFITTER_FORM_PREPARE_DONE,
    },
    [ASSISTANT_TOOL_NAMES.PREPARE_CONTACT_IMPORT]: {
        running: COPY.AI_CHAT_TOOL_CONTACT_IMPORT_PREPARE_RUNNING,
        done: COPY.AI_CHAT_TOOL_CONTACT_IMPORT_PREPARE_DONE,
    },
    [ASSISTANT_TOOL_NAMES.EXECUTE_PREPARED_CONTACT_IMPORT]: {
        running: COPY.AI_CHAT_TOOL_CONTACT_IMPORT_EXECUTE_RUNNING,
        done: COPY.AI_CHAT_TOOL_CONTACT_IMPORT_EXECUTE_DONE,
    },
    [ASSISTANT_TOOL_NAMES.REQUEST_SELECTION]: {
        running: COPY.AI_CHAT_TOOL_REQUEST_SELECTION_RUNNING,
        done: COPY.AI_CHAT_TOOL_REQUEST_SELECTION_DONE,
    },
    [ASSISTANT_TOOL_NAMES.PRESENT_CHART]: {
        running: COPY.AI_CHAT_TOOL_PRESENT_CHART_RUNNING,
        done: COPY.AI_CHAT_TOOL_PRESENT_CHART_DONE,
    },
    [ASSISTANT_TOOL_NAMES.PRESENT_PLAN]: {
        running: COPY.AI_CHAT_TOOL_PRESENT_PLAN_RUNNING,
        done: COPY.AI_CHAT_TOOL_PRESENT_PLAN_DONE,
    },
}

export function buildToolCallLabel(
    part: AnyToolUIPart,
    variant: ToolCallVariant
): string {
    const toolName = getToolName(part)
    const verb = getToolCallVerb({ toolName, variant })
    const subject = getToolCallSubject({ toolName, part, variant })

    return subject ? `${verb} ${subject}` : verb
}

export function getVariantFromState(
    state: string | undefined
): ToolCallVariant | null {
    switch (state) {
        case TOOL_STATES.INPUT_STREAMING:
        case TOOL_STATES.INPUT_AVAILABLE:
        case TOOL_STATES.APPROVAL_REQUESTED:
        case TOOL_STATES.APPROVAL_RESPONDED:
            return TOOL_CALL_VARIANTS.RUNNING
        case TOOL_STATES.OUTPUT_AVAILABLE:
            return TOOL_CALL_VARIANTS.DONE
        case TOOL_STATES.OUTPUT_ERROR:
            return TOOL_CALL_VARIANTS.ERROR
        case TOOL_STATES.OUTPUT_DENIED:
            return TOOL_CALL_VARIANTS.DENIED
        default:
            return null
    }
}

export function hasAiSuggestionId(part: AnyToolUIPart): boolean {
    const output = getOutputRecord(part)
    if (!output) return false
    return Boolean(output.ai_suggestion_id)
}

export function getErrorText(part: AnyToolUIPart): string | undefined {
    if (part.state === TOOL_STATES.OUTPUT_ERROR) {
        return part.errorText || COPY.AI_CHAT_TOOL_CALL_FAILED
    }
    const output = getOutputRecord(part)
    if (!output) return undefined
    if (output.status !== ASSISTANT_TOOL_OUTPUT_STATUSES.ERROR) return undefined
    return toStringOrFallback(output.message, COPY.AI_CHAT_TOOL_CALL_FAILED)
}

export function shouldSuppressToolCallChip({
    part,
    isUserSelectionVisible,
    chartPresentation,
    planPresentation,
    variant,
}: {
    part: AnyToolUIPart
    isUserSelectionVisible: boolean
    chartPresentation: AssistantChartPresentation | null
    planPresentation: AssistantPlanPresentation | null
    variant: ToolCallVariant
}): boolean {
    if (isUserSelectionVisible) return true
    if (chartPresentation) return true
    if (planPresentation) return true

    return isSuccessfulRequestSelection(part, variant)
}

function isSuccessfulRequestSelection(
    part: AnyToolUIPart,
    variant: ToolCallVariant
): boolean {
    if (getToolName(part) !== ASSISTANT_TOOL_NAMES.REQUEST_SELECTION) {
        return false
    }
    if (variant !== TOOL_CALL_VARIANTS.DONE) return false

    const output = getOutputRecord(part)
    return (
        output?.type === ASSISTANT_TOOL_OUTPUT_TYPES.USER_SELECTION &&
        output.status === ASSISTANT_TOOL_OUTPUT_STATUSES.SUCCESS
    )
}

function getToolCallVerb({
    toolName,
    variant,
}: {
    toolName: string
    variant: ToolCallVariant
}): string {
    const verbs = TOOL_SPECIFIC_VERBS[toolName]
    if (variant === TOOL_CALL_VARIANTS.RUNNING && verbs?.running) {
        return verbs.running
    }
    if (variant === TOOL_CALL_VARIANTS.DONE && verbs?.done) {
        return verbs.done
    }
    return VERB_BY_VARIANT[variant]
}

function getToolCallSubject({
    toolName,
    part,
    variant,
}: {
    toolName: string
    part: AnyToolUIPart
    variant: ToolCallVariant
}): string | undefined {
    if (toolName === ASSISTANT_TOOL_NAMES.NAVIGATE_TO_PAGE) {
        return (
            getNavigationDestination(part, variant) ??
            COPY.AI_CHAT_TOOL_NAVIGATE_FALLBACK_DESTINATION
        )
    }
    if (
        toolName === ASSISTANT_TOOL_NAMES.CREATE_MESSAGE_TEMPLATE ||
        toolName === ASSISTANT_TOOL_NAMES.EDIT_EMAIL_TEMPLATE ||
        toolName === ASSISTANT_TOOL_NAMES.PREPARE_OUTFITTER_FORM ||
        toolName === ASSISTANT_TOOL_NAMES.PREPARE_CONTACT_IMPORT ||
        toolName === ASSISTANT_TOOL_NAMES.EXECUTE_PREPARED_CONTACT_IMPORT ||
        toolName === ASSISTANT_TOOL_NAMES.REQUEST_SELECTION ||
        toolName === ASSISTANT_TOOL_NAMES.PRESENT_CHART ||
        toolName === ASSISTANT_TOOL_NAMES.PRESENT_PLAN
    ) {
        return undefined
    }
    const title = "title" in part ? part.title : undefined
    return title ?? humanizeToolName(toolName)
}

function getNavigationDestination(
    part: AnyToolUIPart,
    variant: ToolCallVariant
): string | undefined {
    if (variant === TOOL_CALL_VARIANTS.DONE) {
        const output = getOutputRecord(part)
        if (isNavigationSuccessOutput(output)) {
            return toStringOrFallback(output.destination, undefined)
        }
    }
    const input = getInputRecord(part)
    if (!input) return undefined
    return toStringOrFallback(input.destination, undefined)
}

function isNavigationSuccessOutput(
    value: Readonly<Record<string, unknown>> | null
): value is Readonly<Record<string, unknown>> {
    if (!value) return false
    return (
        value.type === ASSISTANT_TOOL_OUTPUT_TYPES.NAVIGATION &&
        value.status === ASSISTANT_TOOL_OUTPUT_STATUSES.SUCCESS
    )
}

function humanizeToolName(toolName: string): string {
    return toolName.replaceAll("_", " ").toLowerCase()
}

function toStringOrFallback<T extends string | undefined>(
    value: unknown,
    fallback: T
): T | string {
    return typeof value === "string" && value ? value : fallback
}
